export type SquareStoreBillingContact = {
  givenName?: string;
  familyName?: string;
  email?: string;
  phone?: string;
  addressLines?: string[];
  city?: string;
  state?: string;
  countryCode?: string;
  postalCode?: string;
};

/** Square Web Payments verificationDetails for card-on-file (STORE). */
export type SquareStoreVerificationDetails = {
  intent: 'STORE';
  customerInitiated: boolean;
  sellerKeyedIn: boolean;
  billingContact?: SquareStoreBillingContact;
};

export interface SquareCard {
  attach(selector: string): Promise<void>;
  tokenize(verificationDetails?: SquareStoreVerificationDetails): Promise<{
    status: string;
    token?: string;
    errors?: Array<{ message?: string }>;
  }>;
  destroy(): Promise<void>;
}

export interface SquarePayments {
  card(): Promise<SquareCard>;
}

export interface SquareSDK {
  payments(applicationId: string, locationId: string): SquarePayments;
}

declare global {
  interface Window {
    Square?: SquareSDK;
  }
}

const SANDBOX_SRC = 'https://sandbox.web.squarecdn.com/v1/square.js';
const PRODUCTION_SRC = 'https://web.squarecdn.com/v1/square.js';

export function isSquarePaymentProvider(value?: string): boolean {
  return value?.toLowerCase() === 'square';
}

/** Studio staff keys the card for a parent (MOTO / seller-keyed). */
export function buildSellerKeyedStoreVerificationDetails(
  billingContact?: SquareStoreBillingContact,
): SquareStoreVerificationDetails {
  return {
    intent: 'STORE',
    customerInitiated: false,
    sellerKeyedIn: true,
    ...(billingContact && hasBillingContactValues(billingContact)
      ? { billingContact }
      : {}),
  };
}

const hasBillingContactValues = (
  billingContact: SquareStoreBillingContact,
): boolean =>
  Object.values(billingContact).some(
    (value) =>
      (typeof value === 'string' && value.trim().length > 0) ||
      (Array.isArray(value) && value.length > 0),
  );

export async function loadSquareWebPaymentsSdk(
  sandbox: boolean,
): Promise<SquareSDK> {
  if (window.Square) {
    return window.Square;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sandbox ? SANDBOX_SRC : PRODUCTION_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load the Square Web Payments SDK'));
    document.head.appendChild(script);
  });

  if (!window.Square) {
    throw new Error('Square Web Payments SDK did not initialize');
  }

  return window.Square;
}
